<script setup>
import { h, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { useAcesso } from 'dashboard/composables/useAcesso';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import {
  buildPowerAppItems,
  buildPowerAppsGroup,
} from './helpers/powerAppMenu';
import { buildReportsGroup } from './helpers/reportsMenu';
import { buildPowerDataItems } from './helpers/powerDataMenu';
import { buildPowerFlowItems } from './helpers/powerFlowMenu';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
// Platform-staff-only sections (PowerAgents, Campaigns, account/automation/integrations/bots
// settings, and the Bot/CSAT insights) are gated on isSuperAdmin — account admins keep only the
// agent/team/inbox/label/attribute/canned scope.
const { isAdmin, isSuperAdmin } = useAdmin();
// `can` é a faixa do PAPEL PERSONALIZADO, e só ela: para um administrador ela é falsa de
// propósito (o array dele é `['administrator']`). Por isso cada uso abaixo soma com a condição de
// papel que já existia, em vez de substituí-la — ninguém que enxergava uma seção deixa de enxergar.
const { can } = usePermissions();
// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, cada item abaixo decide pelo escopo da rota a que leva, e o
// provider (`isAllowed`) filtra os outros pelo `meta.escopos`; desligada, o `decide` devolve o cheque de hoje. O que é
// da plataforma continua no `isSuperAdmin`.
const { decide } = useAcesso();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

// PA-01 (D17): a faixa de cliente é a flag `power_agents`, que existe em features.yml
// desde julho e nunca foi lida por uma linha de código. Super-admin continua entrando
// sempre; administrador da conta entra quando a flag estiver ligada naquela conta.
const hasPowerAgents = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.POWER_AGENTS
  );
});

// O PowerData decide pelo papel na conta, e não por flag (POWERDATA-ACESSO-01): o atendente
// também entra, e o `isAdmin` sozinho não o enxerga.
const currentRole = useMapGetter('getCurrentRole');

const hasKanban = computed(() => {
  return isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.KANBAN);
});

// CRM-01: the account's boards, listed as children of the Kanban group. Fetched here
// rather than through the kanban store so the sidebar does not depend on the board page
// having been opened first.
//
// Watched instead of fetched onMounted: the account (and therefore the feature flag) is
// not loaded yet on the first render, so an onMounted fetch bails on `hasKanban` being
// false and never retries — which left the group with an empty children array, the state
// that renders "No items" and turns the header into a non-navigable toggle.
const kanbanPipelines = ref([]);
const loadKanbanPipelines = async () => {
  if (!hasKanban.value || !accountId.value) return;
  try {
    const { data } = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/kanban/pipelines`
    );
    kanbanPipelines.value = data.payload || [];
  } catch {
    kanbanPipelines.value = [];
  }
};
watch([accountId, hasKanban], loadKanbanPipelines, { immediate: true });
// Creating or deleting a board happens on the Kanban page, which does not own this list.
emitter.on(BUS_EVENTS.KANBAN_PIPELINES_CHANGED, loadKanbanPipelines);
onUnmounted(() =>
  emitter.off(BUS_EVENTS.KANBAN_PIPELINES_CHANGED, loadKanbanPipelines)
);

// PowerbizzOS app registry: the org's installed PowerApps (loaded per-account).
const powerApps = ref([]);
const loadPowerApps = async () => {
  try {
    const { data } = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/powerapps`
    );
    powerApps.value = buildPowerAppItems(data, accountScopedRoute);
  } catch {
    powerApps.value = [];
  }
};
onMounted(loadPowerApps);

const hasCampaigns = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CAMPAIGNS
  );
});

const hasIntegrations = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.INTEGRATIONS
  );
});

const hasAutomations = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.AUTOMATIONS
  );
});

const hasAgentBots = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.AGENT_BOTS
  );
});

const hasInboxManagement = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.INBOX_MANAGEMENT
  );
});

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Explicit open/close toggle button (monitor-style)
const toggleCollapse = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const menuItems = computed(() => {
  return [
    // A Home e o launcher do OS, e por isso a primeira entrada do menu. Sem `children`:
    // SidebarGroup trata qualquer array (vazio incluido) como expansivel e renderizaria o
    // cabecalho como botao de toggle em vez de router-link.
    {
      name: 'Home',
      icon: 'i-lucide-house',
      label: t('SIDEBAR.HOME'),
      to: accountScopedRoute('os_home'),
      activeOn: ['os_home'],
    },
    // Personal notifications "Inbox" hidden for PowerbizzOS — to be repurposed
    // later as an OS-wide unified inbox (chat + apps + agents).
    {
      name: 'Conversation',
      label: t('SIDEBAR.POWERCHAT'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Participating',
          label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    // CRM-01 — placeholder name, will be renamed once the CRM scope is settled.
    // `children` is only set when there is at least one pipeline: SidebarGroup treats any
    // array (empty included) as "expandable", which renders the header as a toggle button
    // instead of a router-link and shows the "No items" placeholder leaf.
    ...(hasKanban.value
      ? [
          {
            name: 'Kanban',
            icon: 'i-lucide-kanban',
            label: t('SIDEBAR.KANBAN'),
            to: accountScopedRoute('kanban_index'),
            activeOn: ['kanban_index', 'kanban_pipeline'],
            ...(kanbanPipelines.value.length
              ? {
                  children: [
                    ...kanbanPipelines.value.map(pipeline => ({
                      name: `kanban-${pipeline.id}`,
                      label: pipeline.name,
                      to: accountScopedRoute('kanban_pipeline', {
                        pipelineId: pipeline.id,
                      }),
                    })),
                    // Always last, after however many boards exist. A link rather than a
                    // button so it is an ordinary leaf: same permission and feature-flag
                    // resolution, and the create dialog stays reachable by URL.
                    ...(decide('kanban:manage', () => isAdmin.value)
                      ? [
                          {
                            name: 'kanban-new',
                            label: t('KANBAN.PIPELINE.NEW'),
                            icon: 'i-lucide-plus',
                            to: accountScopedRoute('kanban_new_pipeline'),
                          },
                        ]
                      : []),
                  ],
                }
              : {}),
          },
        ]
      : []),
    ...(isSuperAdmin.value ||
    (hasPowerAgents.value &&
      decide(
        'poweragents:read',
        () => isAdmin.value || can('poweragents_manage')
      ))
      ? [
          {
            name: 'PowerAgents',
            icon: 'i-lucide-bot-message-square',
            label: t('SIDEBAR.POWER_AGENTS'),
            to: accountScopedRoute('power_agents_index'),
            // Só a lista: a tela do agente é standalone e não renderiza esta barra, então
            // `power_agents_settings` era um critério que nunca podia casar.
            activeOn: ['power_agents_index'],
          },
        ]
      : []),
    // FLOW-06 (SPEC S5; Gate 1, Q4): a aba PowerFlow, na régua do PowerAgents logo acima.
    ...buildPowerFlowItems({
      t,
      accountScopedRoute,
      isSuperAdmin: isSuperAdmin.value,
      contaComFlag: hasPowerAgents.value,
      isAdmin: isAdmin.value,
      concedidoPorPapel: can('poweragents_manage'),
      decide,
    }),
    ...buildPowerDataItems({
      t,
      accountScopedRoute,
      isSuperAdmin: isSuperAdmin.value,
      role: currentRole.value,
      concedidoPorPapel: can('powerdata_manage'),
      decide,
    }),
    buildPowerAppsGroup(powerApps.value, t('SIDEBAR.APPS'), accountScopedRoute),
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    // Relatórios é um link só, para a página em abas (REPORTS-03). Etiquetas,
    // CSAT e Robôs seguem de pé por link.
    buildReportsGroup({ t, accountScopedRoute }),
    ...(hasCampaigns.value && isSuperAdmin.value
      ? [
          // Uma entrada, não duas. "WhatsApp" e "Outbound Marketing" listavam e criavam o MESMO
          // `Campaign` por caminhos diferentes, e obrigavam a escolher entre elas antes de saber
          // a diferença. `activeOn` cobre a rota de criação e as antigas, que agora redirecionam,
          // para a entrada continuar marcada enquanto se percorre o wizard.
          {
            name: 'Campaigns',
            label: t('SIDEBAR.CAMPAIGNS'),
            icon: 'i-lucide-megaphone',
            to: accountScopedRoute('campaigns_index'),
            activeOn: [
              'campaigns_index',
              'campaigns_new',
              'campaigns_whatsapp_index',
              'campaigns_outbound_index',
            ],
          },
        ]
      : []),
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        ...(isSuperAdmin.value
          ? [
              {
                name: 'Settings Account Settings',
                label: t('SIDEBAR.ACCOUNT_SETTINGS'),
                icon: 'i-lucide-briefcase',
                to: accountScopedRoute('general_settings_index'),
              },
            ]
          : []),
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        // Só administrador: a tela monta papel, e montar papel é entregar permissão. Por isso
        // aqui NÃO entra `can(...)` de nenhuma permissão — não existe delegar o delegador. Com a
        // chave ligada, o `roles:manage`, que também não se concede: só o modelo Administrador o tem.
        ...(isSuperAdmin.value || decide('roles:manage', () => isAdmin.value)
          ? [
              {
                name: 'Settings Custom Roles',
                label: t('SIDEBAR.CUSTOM_ROLES'),
                icon: 'i-lucide-shield-check',
                to: accountScopedRoute('custom_roles_list'),
              },
            ]
          : []),
        {
          name: 'Settings PowerApps',
          label: t('SIDEBAR.POWERAPPS'),
          icon: 'i-lucide-layout-grid',
          to: accountScopedRoute('powerapps_settings_index'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(hasAdvancedAssignment.value && isSuperAdmin.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        ...(hasInboxManagement.value
          ? [
              {
                name: 'Settings Inboxes',
                label: t('SIDEBAR.CHANNELS'),
                icon: 'i-lucide-inbox',
                activeOn: [
                  'settings_inbox_list',
                  'settings_inbox_show',
                  'settings_inbox_new',
                  'settings_inbox_finish',
                  'settings_inboxes_page_channel',
                  'settings_inboxes_add_agents',
                ],
                to: accountScopedRoute('settings_inbox_list'),
              },
            ]
          : []),
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        ...(hasAutomations.value && isSuperAdmin.value
          ? [
              {
                name: 'Settings Automation',
                label: t('SIDEBAR.AUTOMATION'),
                icon: 'i-lucide-repeat',
                to: accountScopedRoute('automation_list'),
              },
            ]
          : []),
        ...(hasAgentBots.value && isSuperAdmin.value
          ? [
              {
                name: 'Settings Agent Bots',
                label: t('SIDEBAR.AGENT_BOTS'),
                icon: 'i-lucide-bot',
                to: accountScopedRoute('agent_bots'),
              },
            ]
          : []),

        ...(isSuperAdmin.value
          ? [
              {
                name: 'Settings Macros',
                label: t('SIDEBAR.MACROS'),
                icon: 'i-lucide-toy-brick',
                to: accountScopedRoute('macros_wrapper'),
              },
            ]
          : []),
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        ...(hasIntegrations.value && isSuperAdmin.value
          ? [
              {
                name: 'Settings Integrations',
                label: t('SIDEBAR.INTEGRATIONS'),
                icon: 'i-lucide-blocks',
                to: accountScopedRoute('settings_applications'),
              },
            ]
          : []),
      ],
    },
  ];
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak md:shadow-[6px_0_24px_rgba(0,0,0,0.05)] dark:md:shadow-[6px_0_24px_rgba(0,0,0,0.4)]"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'flex-col justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
          <button
            type="button"
            :title="t('SIDEBAR.EXPAND_SIDEBAR')"
            :aria-label="t('SIDEBAR.EXPAND_SIDEBAR')"
            class="grid flex-shrink-0 place-content-center size-8 rounded-lg outline outline-1 outline-n-weak text-n-slate-11 transition-colors hover:text-n-brand hover:outline-n-brand"
            @click="toggleCollapse"
          >
            <span class="i-lucide-panel-left-open size-4" />
          </button>
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <Logo class="size-6" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
          <button
            type="button"
            :title="t('SIDEBAR.COLLAPSE_SIDEBAR')"
            :aria-label="t('SIDEBAR.COLLAPSE_SIDEBAR')"
            class="grid flex-shrink-0 place-content-center size-7 rounded-lg outline outline-1 outline-n-weak text-n-slate-11 transition-colors hover:text-n-brand hover:outline-n-brand"
            @click="toggleCollapse"
          >
            <span class="i-lucide-panel-left-close size-4" />
          </button>
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2.5 py-1 w-full h-8 rounded-[10px] outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:outline-n-brand"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-[10px] outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align="start">
          <template #trigger="{ isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
    <a
      href="https://powerbizz.ai"
      target="_blank"
      rel="noopener noreferrer"
      class="text-gray-400 text-xs text-center pb-2 pt-4 hover:text-n-slate-11 transition-colors block no-underline"
    >
      {{ t('SIDEBAR.POWERED_BY') }}
      <span class="text-n-brand font-medium">
        {{ t('SIDEBAR.BRAND') }}{{ t('SIDEBAR.BRAND_TLD') }}
      </span>
    </a>
  </aside>
</template>
